import { X } from "lucide-react";
import { Dialog as RadixDialog } from "radix-ui";
import type { FormEvent, ReactNode } from "react";

import { Button } from "./button";
import { cn } from "./cn";

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** One or two sentences under the title: what this does. */
  description?: ReactNode;
  children: ReactNode;
  /** Buttons, right-aligned (stacked full-width on phones). */
  footer: ReactNode;
  /** The body is a form: Enter submits, and the footer buttons belong to it. */
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  /** Keeps the dialog open while a save is running (no accidental close mid-request). */
  busy?: boolean;
  /** "side": a full-height panel on the right from 640 px up, so the page beside it stays in view. */
  placement?: "center" | "side";
  className?: string;
}

/**
 * A modal dialog: centred on wider screens, a bottom sheet on phones (thumb reach). The body scrolls and the
 * title and buttons stay put. Focus is trapped and returned, Escape closes (Radix).
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  onSubmit,
  busy = false,
  placement = "center",
  className,
}: DialogProps) {
  const body = (
    <>
      <div className="min-h-0 grow overflow-y-auto px-5 py-4 sm:px-6">{children}</div>
      <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-border-subtle px-5 py-4 sm:flex-row sm:justify-end sm:px-6 [&>*]:w-full sm:[&>*]:w-auto">
        {footer}
      </div>
    </>
  );
  return (
    <RadixDialog.Root open={open} onOpenChange={(next) => (busy && !next ? undefined : onOpenChange(next))}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-black/45" />
        <RadixDialog.Content
          className={cn(
            "fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] flex-col rounded-t-2xl border border-border bg-surface shadow-2xl",
            placement === "center"
              ? "sm:inset-auto sm:top-1/2 sm:left-1/2 sm:max-h-[86dvh] sm:w-[min(560px,calc(100vw-32px))] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl"
              : "sm:inset-y-0 sm:right-0 sm:left-auto sm:max-h-none sm:w-[min(480px,calc(100vw-32px))] sm:rounded-none sm:border-y-0 sm:border-r-0",
            className,
          )}
        >
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border-subtle px-5 pt-5 pb-4 sm:px-6">
            <div className="flex flex-col gap-1">
              <RadixDialog.Title className="text-lg font-extrabold tracking-tight">{title}</RadixDialog.Title>
              {description ? (
                <RadixDialog.Description className="text-sm text-muted">
                  {description}
                </RadixDialog.Description>
              ) : (
                <RadixDialog.Description className="sr-only">{title}</RadixDialog.Description>
              )}
            </div>
            <RadixDialog.Close asChild>
              <Button variant="quiet" size="icon" aria-label="Close" className="-mt-2 -mr-2" disabled={busy}>
                <X aria-hidden="true" />
              </Button>
            </RadixDialog.Close>
          </div>
          {onSubmit ? (
            <form noValidate onSubmit={onSubmit} className="flex min-h-0 grow flex-col">
              {body}
            </form>
          ) : (
            body
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
